import { useState, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { supabase } from '@/lib/supabase';

type ScheduleType = 'fixed_time' | 'zman_based' | 'relative' | 'manual';

interface HabitTypeRow {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  schedule_type: ScheduleType;
  default_reminder_offsets: string[];
  default_confirmation_window: string;
  calendar_icon: string;
  max_retries: number;
  custom_settings: Record<string, unknown>;
}

interface UserHabitRow {
  id: string;
  user_id: string;
  habit_type_id: string;
  enabled: boolean;
  reminder_offsets: number[];
  confirmation_window: string;
  notification_providers: string[];
  custom_settings: Record<string, unknown>;
}

interface HabitConfig {
  userHabitId: string | null;
  habitTypeId: string;
  name: string;
  description: string;
  icon: string;
  scheduleType: ScheduleType;
  fixedTime: string;
  zmanReference: string;
  relativeOffset: number;
  relativeAnchor: string;
  enabled: boolean;
  reminderOffsets: number[];
  confirmationWindow: number;
  maxRetries: number;
  notificationProviders: string[];
  busyRetryMinutes: number;
  noAnswerRetryMinutes: number;
  voicemailDetection: boolean;
  callbackAllowed: boolean;
  callbackDeadlineMinutes: number;
}

const zmanReferences = [
  { value: 'alotHashachar', label: 'Alot HaShachar' },
  { value: 'netz', label: 'Netz (Sunrise)' },
  { value: 'sofZmanShemaMga', label: 'Sof Zman Shema (MGA)' },
  { value: 'sofZmanShemaGra', label: 'Sof Zman Shema (GRA)' },
  { value: 'sofZmanTefilla', label: 'Sof Zman Tefilla' },
  { value: 'chatzot', label: 'Chatzot (Midday)' },
  { value: 'minchaGedola', label: 'Mincha Gedola' },
  { value: 'minchaKetana', label: 'Mincha Ketana' },
  { value: 'plagHaMincha', label: 'Plag HaMincha' },
  { value: 'shkia', label: 'Shkia (Sunset)' },
  { value: 'tzet', label: 'Tzet HaKochavim' },
];

const notificationProviderLabels: Record<string, { label: string; icon: string }> = {
  phone: { label: 'Phone Call', icon: 'ri-phone-line' },
  sms: { label: 'SMS', icon: 'ri-message-line' },
  email: { label: 'Email', icon: 'ri-mail-line' },
  whatsapp: { label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  push: { label: 'Push', icon: 'ri-notification-3-line' },
  telegram: { label: 'Telegram', icon: 'ri-telegram-line' },
};

const categoryFromIcon = (icon: string): string => {
  if (icon.includes('sun') || icon.includes('foggy')) return 'morning';
  if (icon.includes('moon') || icon.includes('smartphone')) return 'evening';
  if (icon.includes('book') || icon.includes('graduation') || icon.includes('run') || icon.includes('heart') || icon.includes('quill')) return 'daily';
  return 'custom';
};

const categoryIconBg: Record<string, string> = {
  morning: 'bg-amber-50',
  evening: 'bg-indigo-50',
  daily: 'bg-emerald-50',
  custom: 'bg-secondary-100',
};

const scheduleTypeLabels: Record<string, string> = {
  fixed_time: 'Fixed Time',
  zman_based: 'Zman-Based',
  relative: 'Relative',
  manual: 'Manual',
};

const customHabitIcons = [
  { value: 'ri-sun-line', label: 'Sun' },
  { value: 'ri-moon-line', label: 'Moon' },
  { value: 'ri-run-line', label: 'Run' },
  { value: 'ri-book-open-line', label: 'Book' },
  { value: 'ri-graduation-cap-line', label: 'Study' },
  { value: 'ri-heart-line', label: 'Heart' },
  { value: 'ri-quill-pen-line', label: 'Journal' },
  { value: 'ri-smartphone-line', label: 'Phone' },
  { value: 'ri-mental-health-line', label: 'Mind' },
  { value: 'ri-restaurant-line', label: 'Food' },
  { value: 'ri-water-flash-line', label: 'Water' },
  { value: 'ri-walk-line', label: 'Walk' },
  { value: 'ri-hand-heart-line', label: 'Calm' },
  { value: 'ri-music-line', label: 'Music' },
  { value: 'ri-plant-line', label: 'Plant' },
  { value: 'ri-check-double-line', label: 'Check' },
];

function parseConfirmationWindow(interval: string): number {
  const match = interval.match(/(\d+):(\d+):(\d+)/);
  if (match) return parseInt(match[1]) * 60 + parseInt(match[2]);
  return 30;
}

function parseReminderOffsets(offsets: string[]): number[] {
  return offsets.map((o) => {
    const num = parseInt(o);
    if (o.includes('-')) return -Math.abs(num);
    return Math.abs(num);
  });
}

function defaultHabitConfig(ht: HabitTypeRow): HabitConfig {
  const cs = ht.custom_settings || {};
  return {
    userHabitId: null,
    habitTypeId: ht.id,
    name: ht.name,
    description: ht.description,
    icon: ht.calendar_icon || 'ri-check-line',
    scheduleType: ht.schedule_type || 'fixed_time',
    fixedTime: (cs.ideal_time as string) || '07:00',
    zmanReference: (cs.zman_reference as string) || 'netz',
    relativeOffset: parseInt(String(cs.offset || '0')) || 0,
    relativeAnchor: 'after',
    enabled: false,
    reminderOffsets: parseReminderOffsets(ht.default_reminder_offsets || ['0 minutes']),
    confirmationWindow: parseConfirmationWindow(ht.default_confirmation_window || '00:30:00'),
    maxRetries: ht.max_retries || 2,
    notificationProviders: ['phone'],
    busyRetryMinutes: 5,
    noAnswerRetryMinutes: 15,
    voicemailDetection: true,
    callbackAllowed: true,
    callbackDeadlineMinutes: 120,
  };
}

function mergeWithUserHabit(ht: HabitTypeRow, uh: UserHabitRow | null): HabitConfig {
  if (!uh) return defaultHabitConfig(ht);
  const cs = uh.custom_settings || {};
  return {
    userHabitId: uh.id,
    habitTypeId: ht.id,
    name: ht.name,
    description: ht.description,
    icon: ht.calendar_icon || 'ri-check-line',
    scheduleType: (cs.schedule_type as ScheduleType) || ht.schedule_type || 'fixed_time',
    fixedTime: (cs.fixed_time as string) || (ht.custom_settings?.ideal_time as string) || '07:00',
    zmanReference: (cs.zman_reference as string) || 'netz',
    relativeOffset: parseInt(String(cs.relative_offset || '0')) || 0,
    relativeAnchor: (cs.relative_anchor as string) || 'after',
    enabled: uh.enabled,
    reminderOffsets: Array.isArray(uh.reminder_offsets) ? uh.reminder_offsets : parseReminderOffsets(ht.default_reminder_offsets || ['0 minutes']),
    confirmationWindow: parseConfirmationWindow(uh.confirmation_window || ht.default_confirmation_window || '00:30:00'),
    maxRetries: (cs.max_retries as number) || ht.max_retries || 2,
    notificationProviders: Array.isArray(uh.notification_providers) ? uh.notification_providers : ['phone'],
    busyRetryMinutes: (cs.busy_retry_minutes as number) || 5,
    noAnswerRetryMinutes: (cs.no_answer_retry_minutes as number) || 15,
    voicemailDetection: (cs.voicemail_detection as boolean) ?? true,
    callbackAllowed: (cs.callback_allowed as boolean) ?? true,
    callbackDeadlineMinutes: (cs.callback_deadline_minutes as number) || 120,
  };
}

function HabitCard({ config, expanded, onToggle, onSave, saving }: {
  config: HabitConfig;
  expanded: boolean;
  onToggle: () => void;
  onSave: (updated: HabitConfig) => void;
  saving: boolean;
}) {
  const [localConfig, setLocalConfig] = useState<HabitConfig>({ ...config });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    setLocalConfig({ ...config });
    setSaveStatus('idle');
  }, [config]);

  const hasChanges = JSON.stringify({
    ...localConfig,
    name: undefined,
    description: undefined,
    icon: undefined,
  }) !== JSON.stringify({
    ...config,
    name: undefined,
    description: undefined,
    icon: undefined,
  });

  const updateConfig = (updates: Partial<HabitConfig>) => {
    setLocalConfig((prev) => ({ ...prev, ...updates }));
  };

  const toggleProvider = (provider: string) => {
    const current = localConfig.notificationProviders;
    const updated = current.includes(provider)
      ? current.filter((p) => p !== provider)
      : [...current, provider];
    updateConfig({ notificationProviders: updated });
  };

  const handleSave = async () => {
    setSaveStatus('idle');
    try {
      await onSave(localConfig);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    }
  };

  // Enabling/disabling must persist immediately — otherwise a habit the user
  // "turned off" keeps getting called until they remember to press Save.
  const handleToggleEnabled = async () => {
    const next = { ...localConfig, enabled: !localConfig.enabled };
    setLocalConfig(next);
    setSaveStatus('idle');
    try {
      await onSave(next);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
      setLocalConfig((prev) => ({ ...prev, enabled: !next.enabled }));
    }
  };

  const cat = categoryFromIcon(config.icon);
  const iconBg = categoryIconBg[cat] || 'bg-secondary-100';

  return (
    <div className={`card transition-all hover:shadow-md ${localConfig.enabled ? 'border-l-4 border-l-gold' : 'opacity-70'}`}>
      {/* Header */}
      <button onClick={onToggle} className="group w-full p-4 md:p-5 flex items-center gap-4 text-left cursor-pointer hover:bg-surface-warm/60 dark:hover:bg-white/[0.03] transition rounded-xl">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <i className={`${config.icon} text-foreground-700 text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-sm font-semibold text-foreground-950">{config.name}</h3>
            <span className={`badge ${localConfig.scheduleType === 'zman_based' ? 'badge-zman' : 'badge-fixed'}`}>
              {scheduleTypeLabels[localConfig.scheduleType]}
            </span>
          </div>
          <p className="text-xs text-foreground-500 line-clamp-1">{config.description}</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {localConfig.enabled && (
            <span className="text-[11px] text-gold font-mono">
              {localConfig.scheduleType === 'zman_based'
                ? `${zmanReferences.find((z) => z.value === localConfig.zmanReference)?.label || 'Netz'} ${localConfig.relativeAnchor === 'after' ? '+' : '-'}${localConfig.relativeOffset}min`
                : localConfig.scheduleType === 'fixed_time'
                  ? localConfig.fixedTime
                  : localConfig.scheduleType
              }
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleEnabled();
            }}
            disabled={saving}
            title={localConfig.enabled ? 'Turn off — stops calls immediately' : 'Turn on'}
            className={`relative w-10 h-5 rounded-full transition cursor-pointer flex-shrink-0 ${
              localConfig.enabled ? 'bg-gold' : 'bg-background-300'
            } ${saving ? 'opacity-60' : ''}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition shadow-sm ${
              localConfig.enabled ? 'left-5' : 'left-0.5'
            }`} />
          </button>
          <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-md text-foreground-400 group-hover:text-gold group-hover:bg-gold/10 transition">
            <i className="ri-settings-3-line text-base" />
          </span>
        </div>
      </button>

      {/* Settings drawer */}
      {expanded && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onToggle} />
          <div className="relative w-full sm:max-w-md h-full bg-background-50 shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="h-14 flex items-center justify-between px-5 border-b border-background-200 flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                  <i className={`${config.icon} text-foreground-700`} />
                </span>
                <h3 className="text-sm font-heading font-semibold text-foreground-950 truncate">{config.name}</h3>
              </div>
              <button onClick={onToggle} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-900 hover:bg-background-200 transition cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Schedule */}
          <div>
            <h4 className="text-[0.7rem] font-semibold text-primary-600 uppercase tracking-[0.08em] mb-3 pl-2.5 border-l-2 border-primary-500">Schedule</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Schedule Type</label>
                <select
                  value={localConfig.scheduleType}
                  onChange={(e) => updateConfig({ scheduleType: e.target.value as ScheduleType })}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  <option value="fixed_time">Fixed Time</option>
                  <option value="zman_based">Zman-Based</option>
                  <option value="manual">Manual</option>
                </select>
              </div>

              {localConfig.scheduleType === 'fixed_time' && (
                <div>
                  <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Call Time</label>
                  <input
                    type="time"
                    value={localConfig.fixedTime}
                    onChange={(e) => updateConfig({ fixedTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  />
                </div>
              )}

              {localConfig.scheduleType === 'zman_based' && (
                <>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Zman Reference</label>
                    <select
                      value={localConfig.zmanReference || 'netz'}
                      onChange={(e) => updateConfig({ zmanReference: e.target.value })}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                    >
                      {zmanReferences.map((z) => (
                        <option key={z.value} value={z.value}>{z.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Offset</label>
                    <div className="flex items-center gap-2">
                      <select
                        value={localConfig.relativeAnchor || 'after'}
                        onChange={(e) => updateConfig({ relativeAnchor: e.target.value })}
                        className="px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                      >
                        <option value="after">After</option>
                        <option value="before">Before</option>
                      </select>
                      <input
                        type="number"
                        value={localConfig.relativeOffset || 0}
                        onChange={(e) => updateConfig({ relativeOffset: parseInt(e.target.value) || 0 })}
                        className="w-20 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        min={0}
                      />
                      <span className="text-xs text-foreground-500">min</span>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>

          {/* Reminders & Confirmation */}
          <div>
            <h4 className="text-[0.7rem] font-semibold text-primary-600 uppercase tracking-[0.08em] mb-3 pl-2.5 border-l-2 border-primary-500">Reminders & Confirmation</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Early Reminder</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={Math.max(0, ...localConfig.reminderOffsets.filter((o) => o < 0).map((o) => -o))}
                    onChange={(e) => {
                      const mins = Math.max(0, parseInt(e.target.value) || 0);
                      updateConfig({ reminderOffsets: mins > 0 ? [-mins, 0] : [0] });
                    }}
                    className="w-20 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min={0}
                    max={120}
                  />
                  <span className="text-xs text-foreground-500">min before</span>
                </div>
                <p className="text-[10px] text-foreground-400 mt-1">Rings early, then again at the set time if not done.</p>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Confirmation Window</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localConfig.confirmationWindow}
                    onChange={(e) => updateConfig({ confirmationWindow: parseInt(e.target.value) || 30 })}
                    className="w-20 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min={5}
                  />
                  <span className="text-xs text-foreground-500">min</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Max Retries</label>
                <input
                  type="number"
                  value={localConfig.maxRetries}
                  onChange={(e) => updateConfig({ maxRetries: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                  min={0}
                  max={5}
                />
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div>
            <h4 className="text-[0.7rem] font-semibold text-primary-600 uppercase tracking-[0.08em] mb-3 pl-2.5 border-l-2 border-primary-500">Notification Providers</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(notificationProviderLabels).map(([key, val]) => {
                const isActive = localConfig.notificationProviders.includes(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleProvider(key)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition cursor-pointer whitespace-nowrap border ${
                      isActive
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-background-50 border-background-300 text-foreground-500 hover:border-background-400'
                    }`}
                  >
                    <i className={`${val.icon} text-sm`} />
                    {val.label}
                    {isActive && <i className="ri-check-line text-primary-500" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Advanced */}
          <div>
            <h4 className="text-[0.7rem] font-semibold text-primary-600 uppercase tracking-[0.08em] mb-3 pl-2.5 border-l-2 border-primary-500">Advanced</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Busy Retry</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localConfig.busyRetryMinutes}
                    onChange={(e) => updateConfig({ busyRetryMinutes: parseInt(e.target.value) || 5 })}
                    className="w-16 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min={1}
                  />
                  <span className="text-xs text-foreground-500">min</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">No Answer Retry</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localConfig.noAnswerRetryMinutes}
                    onChange={(e) => updateConfig({ noAnswerRetryMinutes: parseInt(e.target.value) || 10 })}
                    className="w-16 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min={1}
                  />
                  <span className="text-xs text-foreground-500">min</span>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Callback Deadline</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localConfig.callbackDeadlineMinutes}
                    onChange={(e) => updateConfig({ callbackDeadlineMinutes: parseInt(e.target.value) || 60 })}
                    className="w-16 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                    min={0}
                    disabled={!localConfig.callbackAllowed}
                  />
                  <span className="text-xs text-foreground-500">min</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConfig.voicemailDetection}
                    onChange={(e) => updateConfig({ voicemailDetection: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                  />
                  <span className="text-xs text-foreground-700">Voicemail Detection</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localConfig.callbackAllowed}
                    onChange={(e) => updateConfig({ callbackAllowed: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                  />
                  <span className="text-xs text-foreground-700">Allow Callback</span>
                </label>
              </div>
            </div>
          </div>
            </div>{/* end drawer body */}

            {/* Drawer footer */}
            <div className="px-5 py-3.5 bg-background-50 border-t border-background-200 flex items-center gap-3 flex-shrink-0">
              {saveStatus === 'saved' && (
                <span className="text-xs text-emerald-600 flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-check-line" /> Saved
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-xs text-red-500 flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-error-warning-line" /> Save failed
                </span>
              )}
              <button
                onClick={onToggle}
                className="px-4 py-2.5 text-sm rounded-md font-medium text-foreground-600 hover:bg-background-200 transition cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`flex-1 text-sm px-4 py-2.5 rounded-lg font-semibold transition whitespace-nowrap cursor-pointer ${
                  hasChanges && !saving
                    ? 'bg-gold text-midnight hover:shadow-gold dark:bg-electric dark:text-white'
                    : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                }`}
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin" /> Saving...
                  </span>
                ) : hasChanges ? 'Save Changes' : 'Saved'}
              </button>
            </div>
          </div>{/* end drawer panel */}
        </div>
      )}
    </div>
  );
}

function AddHabitModal({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('fixed_time');
  const [fixedTime, setFixedTimeLocal] = useState('08:00');
  const [zmanRef, setZmanRef] = useState('netz');
  const [zmanAnchor, setZmanAnchor] = useState<'before' | 'after'>('before');
  const [zmanOffset, setZmanOffset] = useState(15);
  const [selectedIcon, setSelectedIcon] = useState('ri-check-double-line');
  const [saving, setSavingLocal] = useState(false);
  const [error, setModalError] = useState<string | null>(null);

  if (!open) return null;

  const handleCreate = async () => {
    if (!user || !name.trim()) return;
    setSavingLocal(true);
    setModalError(null);
    try {
      const offsetStr = `${zmanAnchor === 'before' ? '-' : '+'}${zmanOffset}m`;
      const customSettings: Record<string, unknown> = { ideal_time: fixedTime };
      if (scheduleType === 'zman_based') {
        customSettings.zman_reference = zmanRef;
        customSettings.offset = offsetStr;
      }

      const { data: newType, error: insertTypeErr } = await supabase
        .from('habit_types')
        .insert({
          name: name.trim(),
          description: description.trim() || `Custom habit: ${name.trim()}`,
          enabled: true,
          schedule_type: scheduleType,
          calendar_icon: selectedIcon,
          default_reminder_offsets: ['0 minutes'],
          default_confirmation_window: '00:30:00',
          max_retries: 2,
          custom_settings: customSettings,
        })
        .select('id')
        .single();

      if (insertTypeErr) throw insertTypeErr;
      if (!newType) throw new Error('Failed to create habit type');

      const { error: insertUhErr } = await supabase
        .from('user_habits')
        .insert({
          user_id: user.id,
          habit_type_id: newType.id,
          enabled: true,
          reminder_offsets: [0],
          confirmation_window: '00:30:00',
          notification_providers: ['phone'],
          custom_settings: {
            schedule_type: scheduleType,
            fixed_time: fixedTime,
            zman_reference: zmanRef,
            offset: scheduleType === 'zman_based' ? offsetStr : undefined,
            relative_offset: zmanAnchor === 'before' ? -zmanOffset : zmanOffset,
            relative_anchor: zmanAnchor,
            max_retries: 2,
            busy_retry_minutes: 5,
            no_answer_retry_minutes: 15,
            voicemail_detection: true,
            callback_allowed: true,
            callback_deadline_minutes: 120,
          },
        });

      if (insertUhErr) throw insertUhErr;

      setName('');
      setDescription('');
      setFixedTimeLocal('08:00');
      setScheduleType('fixed_time');
      setSelectedIcon('ri-check-double-line');
      onCreated();
      onClose();
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : 'Failed to create habit');
    } finally {
      setSavingLocal(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md h-full bg-background-50 shadow-2xl flex flex-col">
        <div className="h-14 flex items-center justify-between px-5 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-gold/15 text-gold flex-shrink-0">
              <i className="ri-add-line" />
            </span>
            <h3 className="text-sm font-heading font-semibold text-foreground-950">New Habit</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-foreground-900 hover:bg-background-200 transition cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Habit Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Drink Water, Stretch, Read..."
              maxLength={100}
              className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this habit..."
              maxLength={200}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
            />
          </div>

          {/* Schedule Type & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Schedule Type</label>
              <select
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
                className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
              >
                <option value="fixed_time">Fixed Time</option>
                <option value="zman_based">Zman-Based</option>
                <option value="manual">Manual Only</option>
              </select>
            </div>
            {scheduleType === 'fixed_time' && (
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Call Time</label>
                <input
                  type="time"
                  value={fixedTime}
                  onChange={(e) => setFixedTimeLocal(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
              </div>
            )}
            {scheduleType === 'zman_based' && (
              <div>
                <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Zman Reference</label>
                <select
                  value={zmanRef}
                  onChange={(e) => setZmanRef(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  {zmanReferences.map((z) => (
                    <option key={z.value} value={z.value}>{z.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Zman offset */}
          {scheduleType === 'zman_based' && (
            <div>
              <label className="block text-[11px] font-medium text-foreground-600 mb-1.5">Call me</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={zmanOffset}
                  onChange={(e) => setZmanOffset(Math.max(0, parseInt(e.target.value) || 0))}
                  min={0}
                  className="w-20 px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                />
                <span className="text-xs text-foreground-500">minutes</span>
                <select
                  value={zmanAnchor}
                  onChange={(e) => setZmanAnchor(e.target.value as 'before' | 'after')}
                  className="px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent cursor-pointer"
                >
                  <option value="before">before</option>
                  <option value="after">after</option>
                </select>
                <span className="text-xs text-foreground-500">
                  {zmanReferences.find((z) => z.value === zmanRef)?.label}
                </span>
              </div>
            </div>
          )}

          {/* Icon Picker */}
          <div>
            <label className="block text-[11px] font-medium text-foreground-600 mb-2">Icon</label>
            <div className="grid grid-cols-8 gap-2">
              {customHabitIcons.map((icon) => (
                <button
                  key={icon.value}
                  type="button"
                  onClick={() => setSelectedIcon(icon.value)}
                  title={icon.label}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition cursor-pointer ${
                    selectedIcon === icon.value
                      ? 'bg-gold text-midnight ring-1 ring-gold'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
                  }`}
                >
                  <i className={icon.value} />
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-red-500 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {error}
            </p>
          )}

        </div>{/* end scroll body */}

        {/* Footer */}
        <div className="px-5 py-3.5 bg-background-50 border-t border-background-200 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-foreground-600 hover:bg-background-200 rounded-md transition cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || saving}
            className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-lg transition cursor-pointer whitespace-nowrap ${
              name.trim() && !saving
                ? 'bg-gold text-midnight hover:shadow-gold dark:bg-electric dark:text-white'
                : 'bg-background-200 text-foreground-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin" /> Creating...
              </span>
            ) : 'Create Habit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HabitsContent() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [habits, setHabits] = useState<HabitConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const loadHabits = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const [{ data: types, error: typesErr }, { data: userHabits, error: uhErr }] = await Promise.all([
        supabase.from('habit_types').select('*').eq('enabled', true).order('name'),
        supabase.from('user_habits').select('*').eq('user_id', user.id),
      ]);
      if (typesErr) throw typesErr;
      if (uhErr) throw uhErr;

      const uhMap: Record<string, UserHabitRow> = {};
      (userHabits || []).forEach((uh: UserHabitRow) => {
        uhMap[uh.habit_type_id] = uh;
      });

      const merged = (types || []).map((ht: HabitTypeRow) => mergeWithUserHabit(ht, uhMap[ht.id] || null));
      setHabits(merged);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load habits');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadHabits();
  }, [loadHabits]);

  const handleSave = async (updated: HabitConfig) => {
    if (!user) return;
    setSavingId(updated.habitTypeId);

    const customSettings = {
      schedule_type: updated.scheduleType,
      fixed_time: updated.fixedTime,
      zman_reference: updated.zmanReference,
      // The scheduler reads `offset` as a "+15m"/"-30m" string
      offset: `${updated.relativeAnchor === 'before' ? '-' : '+'}${Math.abs(updated.relativeOffset || 0)}m`,
      relative_offset: updated.relativeOffset,
      relative_anchor: updated.relativeAnchor,
      max_retries: updated.maxRetries,
      busy_retry_minutes: updated.busyRetryMinutes,
      no_answer_retry_minutes: updated.noAnswerRetryMinutes,
      voicemail_detection: updated.voicemailDetection,
      callback_allowed: updated.callbackAllowed,
      callback_deadline_minutes: updated.callbackDeadlineMinutes,
    };

    const payload = {
      user_id: user.id,
      habit_type_id: updated.habitTypeId,
      enabled: updated.enabled,
      reminder_offsets: updated.reminderOffsets,
      confirmation_window: `${String(Math.floor(updated.confirmationWindow / 60)).padStart(2, '0')}:${String(updated.confirmationWindow % 60).padStart(2, '0')}:00`,
      notification_providers: updated.notificationProviders,
      custom_settings: customSettings,
    };

    if (updated.userHabitId) {
      const { error: updateErr } = await supabase
        .from('user_habits')
        .update(payload)
        .eq('id', updated.userHabitId);
      if (updateErr) throw updateErr;
    } else {
      const { data: inserted, error: insertErr } = await supabase
        .from('user_habits')
        .insert(payload)
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      setHabits((prev) =>
        prev.map((h) =>
          h.habitTypeId === updated.habitTypeId ? { ...updated, userHabitId: inserted.id } : h
        )
      );
    }

    setHabits((prev) =>
      prev.map((h) =>
        h.habitTypeId === updated.habitTypeId ? { ...updated, userHabitId: updated.userHabitId } : h
      )
    );
  };

  const filteredHabits = habits.filter((h) =>
    filterCategory === 'all' ? true : categoryFromIcon(h.icon) === filterCategory
  );

  const enabledCount = habits.filter((h) => h.enabled).length;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
          <p className="text-sm text-foreground-500">Loading habits...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-600">{error}</p>
          <button onClick={loadHabits} className="text-xs px-4 py-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 transition cursor-pointer whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
      <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-300/40 bg-cream/60 dark:bg-midnight-deep/60 backdrop-blur flex-shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-xl font-bold text-foreground-950">Habits</h2>
          <span className="badge badge-gold font-semibold">
            {enabledCount} active
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center gap-1.5 text-xs px-3.5 py-2"
          >
            <i className="ri-add-line" /> Add Custom Habit
          </button>
          <button
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-500 hover:text-gold hover:bg-gold/10 transition cursor-pointer"
            title="Toggle dark mode"
          >
            <i className={theme === 'dark' ? 'ri-sun-line text-lg' : 'ri-moon-line text-lg'} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {/* Category filters */}
        <div className="flex items-center gap-1 mb-5 bg-background-100 rounded-full p-1 w-fit">
          {[
            { value: 'all', label: 'All' },
            { value: 'morning', label: 'Morning' },
            { value: 'daily', label: 'Daily' },
            { value: 'evening', label: 'Evening' },
          ].map((cat) => (
            <button
              key={cat.value}
              onClick={() => setFilterCategory(cat.value)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition cursor-pointer whitespace-nowrap ${
                filterCategory === cat.value
                  ? 'bg-gold text-midnight shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Habits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredHabits.map((habit) => (
            <HabitCard
              key={habit.habitTypeId}
              config={habit}
              expanded={expandedId === habit.habitTypeId}
              onToggle={() => setExpandedId(expandedId === habit.habitTypeId ? null : habit.habitTypeId)}
              onSave={handleSave}
              saving={savingId === habit.habitTypeId}
            />
          ))}
        </div>

        {filteredHabits.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
              <i className="ri-list-check-3 text-2xl text-foreground-400" />
            </div>
            <p className="text-sm text-foreground-500">No habits in this category</p>
          </div>
        )}
      </div>

      <AddHabitModal open={showAddModal} onClose={() => setShowAddModal(false)} onCreated={loadHabits} />
    </div>
  );
}

export default function HabitsPage() {
  return (
    <AuthGuard>
      <AppLayout>
        <HabitsContent />
      </AppLayout>
    </AuthGuard>
  );
}